import React, { useCallback, useEffect, useRef, useState } from "react";
import LayoutPage from "../../../templates/Layout/LayoutPage";
import { ContentTopStyles, ContentBottomStyles } from "./DistrictInsight.styles";
import {Grid, Box, Paper} from "@mui/material";
import { DistrictInsightContext } from "../../../utils/Context";
import DistrictCard from "../../../components/organisms/DistrictCard/DistrictCard";
import {currencyFormat, fetchData, filterCards, yearList} from "../../../utils/helpsers";
import ErrorLog from "../../../components/molecules/ErrorLog/ErrorLog";
import MapView from "../../../components/organisms/MapView/MapView";
import DistrictInsightBoardMenu from "./DistrictInsightBoardMenu";
import ComparisonCard from "../../../components/organisms/ComparisonCard/ComparisonCard";
import TornadoChart from "../../../components/organisms/TornadoChart/TornadoChart";
import PieChart from "../../../components/organisms/PieChart/PieChart";
import ColumnChart from "../../../components/organisms/ColumnChart/ColumnChart";
import RadarChart from "../../../components/organisms/RadarChart/RadarChart";
import ReportButtons from "../../../components/organisms/BugReport/ReportButtons";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import GroupDisplay from "../../../components/molecules/GroupDisplay/GroupDisplay";

function DistrictInsightBoard() {
  // User environment.
  const env = process.env.NODE_ENV;

  // User session metadata.
  const { user } = useAuth0();
  const navigate = useNavigate();
  const metadata = user[`${process.env.REACT_APP_AUTH0_KEY}/district`];
  const enabledHealthBenefits = metadata.enable_hbenefits;

  // Data sett by the board builder.
  const [districtInsightData, setDistrictInsightData] = useState();

  // District Data hook (used on: District Card).
  const [districtInformation, setDistrictInformation] = useState();

  // Map data.
  const [mapData, setMapData] = useState();

  // DemographicsData charts.
  const [demographicsData, setDemographicsData] = useState();
  const [demographicsGroupData, setDemographicsGroupData] = useState();

  // District Per Pupil Spending chart.
  const [perPupilSpendingData, setPerPupilSpendingData] = useState();
  const [totalSpending, setTotalSpending] = useState();

  // Group Per Pupil spending chart.
  const [perPupilSpendingGroupData, setPerPupilSpendingGroupData] = useState();
  const [totalSpendingGroup, setTotalSpendingGroup] = useState();

  // Enrollment Brackets chart.
  const [enrollmentBrackets, setEnrollmentBrackets] = useState();

  // Academic Performance chart (radar).
  const [academicPerformance, setAcademicPerformance] = useState();

  // Data to feed Comparison cards.
  const [cardOptions, setCardOptions] = useState();
  const [comparisonCardsData, setComparisonCardsData] = useState();

  const comparisonCardsInfo = useRef({
    cardsData: {},
  });

  const updateStoredCardData = useCallback((val, key) => {
    if (val) {
      comparisonCardsInfo.current.cardsData = {
        ...comparisonCardsInfo.current.cardsData,
        [`card${key}`]: val,
      }
    }
  }, [])

  useEffect(() => {
    if (metadata) {
      const userSessionType = metadata.hasOwnProperty('sessionType') ? metadata.sessionType : 'forbidden';
      switch (userSessionType) {
        case 'district-access':
          window.localStorage.setItem('session-type', 'district-access');
          break;
        case 'full-access':
          window.localStorage.setItem('session-type', 'full-access');
          break;
        case 'multiple-district-access':
          window.localStorage.setItem('session-type', 'multiple-district-access');
          break;
        case 'forbidden':
          navigate("/AccessDenied", { replace: true });
          break;
        default:
          navigate("/AccessDenied", { replace: true });
          break;
      }
    } else {
      navigate("/AccessDenied", { replace: true });
    }
  }, [navigate, user, metadata]);

  useEffect(() => {
    // Cleaning hooks.
    if (!districtInsightData) {
      setDistrictInformation();
      setMapData();
      setDemographicsData();
      setDemographicsGroupData();
      setEnrollmentBrackets();
      setAcademicPerformance();
      setComparisonCardsData();
      setCardOptions();
    }
  }, [districtInsightData])

  // Fetching District Information.
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.referenceDistrict && districtInsightData?.year.value) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_resume?_irn=${(districtInsightData.referenceDistrict).value}&_year=${districtInsightData.year.value}`,
        controller: abortController,
      }).then(response => {
        let data = response.data[0];
        if (typeof data === 'object') {
          setDistrictInformation(data)
        } else {
          setDistrictInformation({ type: 'Data Error', message: `Invalid type data type ${data}, expected an object` })
        }
      }).catch(error => setDistrictInformation({ type: 'Fetch Error', ...error }))
    }

    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData?.referenceDistrict, districtInsightData?.year.value])


  // Fetching Map data.
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.referenceDistrict && districtInsightData?.year.value && districtInsightData?.irns) {
      if (districtInsightData?.year.value === 'MCD') {
        fetchData({
          endpoint: `${process.env.REACT_APP_API_URL}/hvd_polygons_mcd?irn=in.(${districtInsightData?.irns},${(districtInsightData.referenceDistrict).value})`,
          controller: abortController,
        }).then(response => {
          let data = response.data;
          if (typeof data === 'object') {
            setMapData(data)
          } else {
            setMapData({ type: 'Data Error', message: `Invalid type data type ${data}, expected an object` })
          }
        }).catch(error => setMapData({ type: 'Fetch Error', ...error }))
      } else {
        fetchData({
          endpoint: `${process.env.REACT_APP_API_URL}/hvd_polygons?irn=in.(${districtInsightData?.irns},${(districtInsightData.referenceDistrict).value})&file_year=eq.${districtInsightData.year.value}`,
          controller: abortController,
        }).then(response => {
          let data = response.data;
          if (typeof data === 'object') {
            setMapData(data)
          } else {
            setMapData({ type: 'Data Error', message: `Invalid type data type ${data}, expected an object` })
          }
        }).catch(error => setMapData({ type: 'Fetch Error', ...error }))
      }      
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData?.referenceDistrict, districtInsightData?.year.value, districtInsightData?.irns])

  // Fetching Student Demographics (tornado chart).
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.referenceDistrict && districtInsightData?.year.value) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_tornado?_years=` + districtInsightData.year.value + "&_irns=" + districtInsightData.referenceDistrict.value,
        controller: abortController,
      }).then(response => {
        let tornadoData = response.data[0];
        if(districtInsightData.year.value === 'MCD'){
          tornadoData.categories = (tornadoData.categories).map( cat => cat.replace(`(${yearList[1].label})`, ''))
        }
        setDemographicsData(tornadoData);
      }).catch(error => setMapData({ type: 'Fetch Error', ...error }));
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData?.referenceDistrict, districtInsightData?.year.value]);

  // Fetching Group Demographics (tornado chart).
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.referenceDistrict && districtInsightData?.year.value && districtInsightData.irns) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_tornado?_irns=` + districtInsightData.irns + '&_years=' + districtInsightData.year.value,
        controller: abortController,
      }).then(response => {
        let tornadoData = response.data[0];
        if(districtInsightData.year.value === 'MCD'){
          tornadoData.categories = (tornadoData.categories).map( cat => cat.replace(`(${yearList[1].label})`, ''))
        }
        setDemographicsGroupData(tornadoData);
      }).catch(error => setMapData({ type: 'Fetch Error', ...error }));
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData?.referenceDistrict, districtInsightData?.year.value, districtInsightData?.irns])

  // Fetching Per Pupil Spending (pie chart).
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.referenceDistrict && districtInsightData?.year.value) {
      // Fetching Per Pupil Spending.
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_per_pupil_spending?_irns=${districtInsightData.referenceDistrict.value}&_year=${districtInsightData.year.value}`,
        controller: abortController,
      }).then(response => {
        const totalSpendingPerPupil = (response.data).filter(item => (item.name).includes("District Total"));
        response.data = (response.data).filter(item => !(item.name).includes("District Total"));
        if(districtInsightData.year.value === 'MCD') {
          Object.keys(response.data).forEach(key => {
            response.data[key].name = (response.data[key].name).replace(`(${yearList[1].label})`, '');
          });
        }
        setPerPupilSpendingData(response.data);
        setTotalSpending(totalSpendingPerPupil[0]);
      }).catch(error => setMapData({ type: 'Fetch Error', ...error }));
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData?.referenceDistrict, districtInsightData?.year.value])

  // Fetching Group Per Pupil Spending (pie chart)
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.irns && districtInsightData?.year.value) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_per_pupil_spending?_irns=${districtInsightData.irns}&_year=${districtInsightData.year.value}`,
        controller: abortController,
      }).then(response => {
        let data = response.data;
        const totalSpendingPerPupil = data.filter(item => (item.name).includes("District Total"));
        data = data.filter(item => !(item.name).includes("District Total"));
        if(districtInsightData.year.value === 'MCD') {
          Object.keys(data).forEach(key => {
            data[key].name = (data[key].name).replace(`(${yearList[1].label})`, '');
          });
        }
        setPerPupilSpendingGroupData(data);
        setTotalSpendingGroup(totalSpendingPerPupil[0]);
      }).catch(error => setMapData({ type: 'Fetch Error', ...error }));
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData?.irns, districtInsightData?.year.value])

  // Enrollment Bracket chart.
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInformation?.group_name && districtInsightData?.year.value) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_enrollment_bracket?_year=${districtInsightData.year.value}`,
        controller: abortController,
      }).then(response => (response.data).forEach(item => {
        item.data.forEach(itm => {
          if (itm.name === districtInformation.group_name) {
            itm.selected = true
            setEnrollmentBrackets(null)
            return setEnrollmentBrackets(response.data);
          }
        });
        item.data_sum.forEach(itm => {
          if (itm.name === districtInformation.group_name) {
            itm.selected = true
          }
        });
      }));
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();
  }, [districtInformation, districtInsightData?.year.value])

  // Academic Performance chart.
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData?.referenceDistrict && districtInsightData?.year.value) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_radars?_irn=${(districtInsightData.referenceDistrict).value}&_irns=${districtInsightData.irns}&_year=${districtInsightData.year.value}`,
        controller: abortController,
      }).then(response => {
        setAcademicPerformance(response.data);
      });
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();
  }, [districtInsightData?.referenceDistrict, districtInsightData?.year.value, districtInsightData?.irns])


  // Fetching Comparison Cards dataObject.
  useEffect(() => {
    let abortController = new AbortController();
    if (districtInsightData) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_domain_card_temp?_irn=${(districtInsightData.referenceDistrict).value}&_irns=${districtInsightData.irns}&_year=${districtInsightData.year.value}`,
        controller: abortController,
      }).then(response => {
        setComparisonCardsData(response.data);
      });
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [districtInsightData])

  useEffect(() => {
    let abortController = new AbortController();
    if (comparisonCardsData && districtInsightData) {
      let fetchedCardOptions = {};

      comparisonCardsData.forEach((card, index, arr) => {
        if (card.domain === 'Teacher Salary and Benefits') {
          if (districtInsightData.year.value === 'MCD') {
            fetchData({
              endpoint: `${process.env.REACT_APP_API_URL}/mcd_sv_health?domain=eq.${card.domain}&erirn_employer_ein=eq.${(districtInsightData.referenceDistrict).value}&q13_plantype_p7q3=eq.2&select=label,value:value_var,year:file_year&visible=eq.true`,
              controller: abortController,
            }).then(response => {
              (response.data).forEach( (el, i) => {
                // Some variables include the year in the label, in this case the year is nor added.
                if(el.year && !response.data[i]['label'].includes(el.year)){
                  response.data[i]['label'] = `${response.data[i]['label']} (${response.data[i]['year']})`
                }
              })
              fetchedCardOptions[card.domain] = response.data;
              if (response.data.length > 0 && arr.length === Object.keys(fetchedCardOptions).length) {
                setCardOptions(fetchedCardOptions)
              }
            });
          } else {
            fetchData({
              endpoint: `${process.env.REACT_APP_API_URL}/hvd_sv_health?domain_name=eq.${card.domain}&erirn_employer_ein=eq.${(districtInsightData.referenceDistrict).value}&file_year=eq.${districtInsightData.year.value}&q13_plantype_p7q3=eq.2&select=label,value:value_var&visible=eq.true`,
              controller: abortController,
            }).then(response => {
              fetchedCardOptions[card.domain] = response.data;
              if (response.data.length > 0 && arr.length === Object.keys(fetchedCardOptions).length) {
                setCardOptions(fetchedCardOptions)
              }
            });
          }
        } else {
          let domainEndpoint = districtInsightData.year.value === 'MCD'
              ? `${process.env.REACT_APP_API_URL}/rpc/historical_variables_mcd?_domain=${card.domain}&_irn=${districtInsightData.referenceDistrict.value}&order=var_order`
              : `${process.env.REACT_APP_API_URL}/historical_variable?domain=eq.${card.domain}&select=label:human_name,value:dwh_variable&order=var_order&visible=eq.true`

          fetchData({
            endpoint: domainEndpoint,
            controller: abortController,
          }).then( response => {
            // If MCD update the label to display the year.
            if (districtInsightData.year.value === 'MCD') {
              (response.data).forEach( (el, i) => {
                // Teacher Salary vars do not require display the year.
                if(el.year && el.dom !== 'Teacher Salary'){
                  response.data[i]['label'] = `${response.data[i]['label']}${response.data[i]['year']}`
                }
              })
            }
            fetchedCardOptions[card.domain] = response.data;
            if (arr.length === Object.keys(fetchedCardOptions).length) {
              setCardOptions(fetchedCardOptions)
            }
          })
        }
      });
    }
    // Aborts the request when the component unmounts.
    return () => abortController?.abort();

  }, [comparisonCardsData, districtInsightData]);

  /**
   * This function was created to update the var on year 2022 for specific card.
   * Ticket: Issue #1005
   */
  function updateCardDefaultVar({ domain, defaultVar }) {
    switch (domain) {
      case 'Enrollment':
        return { label: "Enrolled Average Daily Membership (ADM)", value: "total_year_end_enrollment_fy1" }
      case 'Property Tax Base':
        return { label: 'Residential Property Assessed Value', value: 'assessed_value_residential' }
      case 'Student Composition':
        return { label: 'Disadvantaged Students (% Enrollment)', value: 'percent_of_disadvantaged_students_fy1' }
      default:
        return defaultVar
    }
  }

  return (
    <DistrictInsightContext.Provider value={{ districtInsightData, setDistrictInsightData }}>
      <LayoutPage
        pageTitle="District Insights"
        description="Build custom groups based on filters"
        container="fixed"
        type="dashboard"
        customClass="district-insights"
        topContent={<DistrictInsightBoardMenu activeBoardData={comparisonCardsInfo} />}
      >
        <Grid container spacing={2} columns={12} className="content__top" sx={ContentTopStyles}>
          { // DistrictCard Component.
            districtInformation &&
            <Grid item xs={12} lg={6} className="section section--district-data">
              {
                districtInformation.hasOwnProperty('type')
                  ? <ErrorLog errorInfo={districtInformation} component={'District Card'} environment={env} />
                  : <DistrictCard data={districtInformation} />
              }
            </Grid>
          }
          { // Map Component.
            (mapData && districtInsightData?.referenceDistrict.value) &&
            <Grid item xs={12} lg={6} className="section section--district-map">
              {
                mapData.hasOwnProperty('type')
                  ? <ErrorLog errorInfo={mapData} component={'Map Data'} environment={env} />
                  : <MapView data={mapData} districtIrn={(districtInsightData.referenceDistrict).value} />
              }
            </Grid>
          }
          { // Tornado Chart: Student Demographics.
            (demographicsData && districtInsightData?.year.value) && (
              <Grid item xs={12} md={6} className="section section--student-demographics">
                <Box component={Paper} elevation={2} className="section__wrapper">
                  <div className={'chart-title'}>{`${districtInsightData.referenceDistrict.label}: Demographics`}</div>
                  <TornadoChart
                    data={demographicsData}
                    title={''}
                    subtitle="Student vs Teachers % of Total" />
                </Box>
              </Grid>
            )
          }
          { // Tornado Chart: Group Demographics.
            (demographicsGroupData && districtInsightData?.year.value) && (
              <Grid item xs={12} md={6} className="section section--group-demographics">
                <Box component={Paper} elevation={2} className="section__wrapper">
                  <div className={'chart-title'}><GroupDisplay groupData={districtInsightData}/>{': Demographics'}</div>
                  <TornadoChart
                    data={demographicsGroupData}
                    title={''}
                    subtitle="Student vs Teachers % of Total"
                  />
                </Box>
              </Grid>
            )
          }
          { // Pie chart: District Per Pupil Spending.
            (perPupilSpendingData && districtInsightData?.referenceDistrict.label) && (
              <Grid item xs={12} md={6} className="section section--pupil-spending">
                <Box component={Paper} elevation={2} className="section__wrapper">
                  <div className={'chart-title'}>{`${districtInsightData.referenceDistrict.label}: Per Pupil Spending`}</div>
                  <PieChart
                    data={perPupilSpendingData}
                    title={''}
                    subtitle={" Expenditure per Pupil $USD"}
                    id={"district-spending"}
                    toggle={true}
                  />
                  {totalSpending && (
                    <div className="extra-data">
                      {" "}
                      Total Spending Per Pupil:{" "}
                      <strong>{currencyFormat(totalSpending.y)}</strong>
                    </div>
                  )}
                </Box>
              </Grid>
            )
          }
          { // Pie chart: Group Per Pupil Spending.
            (perPupilSpendingGroupData && districtInsightData && districtInsightData?.year.value) && (
              <Grid item xs={12} md={6} className="section section--pupil-group-spending">
                <Box component={Paper} elevation={2} className="section__wrapper">
                  <div className={'chart-title'}><GroupDisplay groupData={districtInsightData}/>{': Per Pupil Spending'}</div>
                  <PieChart
                    data={perPupilSpendingGroupData}
                    title={''}
                    subtitle={" Expenditure per Pupil $USD"}
                    id={"group-demographics"}
                    toggle={true}
                  />
                  {totalSpendingGroup && (
                    <div className="extra-data">
                      {" "}
                      Total Spending Per Pupil:{" "}
                      <strong>{currencyFormat(totalSpendingGroup.y)}</strong>
                    </div>
                  )}
                </Box>
              </Grid>
            )
          }
          { // Bar chart: Enrollment Brackets.
            (enrollmentBrackets) && (
              <Grid item xs={12} md={6} className="section section--enrollment-brackets">
                <Box component={Paper} elevation={2} className="section__wrapper">
                  <ColumnChart
                    data={enrollmentBrackets}
                    title={"Enrollment Brackets"}
                  />
                </Box>
              </Grid>
            )}
          { // Radar chart: Academic Performance.
            (academicPerformance && districtInsightData) && (
              <Grid item xs={12} md={6} className="section section--academic-performance">
                <Box component={Paper} elevation={2} className="section__wrapper">
                  <RadarChart
                    data={academicPerformance}
                    title={"Academic Performance"}
                    referenceDistrict={districtInsightData.referenceDistrict.label}
                    groupName={districtInsightData.groupInfo.label ?? 'Comparison Group'}
                  />
                </Box>
              </Grid>
            )}
        </Grid>
        <Box className={"content__bottom"} sx={ContentBottomStyles}>
          <Grid container component={'ul'} spacing={2} columns={12} className="section--comparison-cards">
            {
              (comparisonCardsData && districtInsightData?.year.value && cardOptions) && (
                    filterCards({arr: comparisonCardsData, flag: enabledHealthBenefits}).map((item, key) => {
                    return (
                        <Grid item component={'li'} xs={12} md={6} lg={4} key={key} className="comparison-card__item">
                          <ComparisonCard
                              key={key}
                              defaultDomainVar={
                                districtInsightData?.comparisonCards?.cardsData
                                    ? districtInsightData.comparisonCards.cardsData[`card${key}`]
                                    : ()=> {
                                      // This if function was added to avoid null on first load for specific domains.
                                      if(districtInsightData?.year.value === 2022) {
                                        return updateCardDefaultVar({domain: item.domain, defaultVar: cardOptions[item.domain][0]})
                                      } else {
                                        return cardOptions[item.domain][0]
                                      }
                                    }
                              }
                              id={key}
                              year={districtInsightData.year.value}
                              title={item.domain}
                              irns={(districtInsightData.irns).toString()}
                              referenceDistrict={districtInsightData.referenceDistrict}
                              groupName={districtInsightData.groupInfo.label ?? 'Comparison Group'}
                              groupDefinition={<GroupDisplay groupData={districtInsightData}/>}
                              groupType={districtInsightData.groupType}
                              domainOptions={cardOptions[item.domain]}
                              storedCardInfo={updateStoredCardData}
                          />
                        </Grid>
                    )
                  })
              )}
          </Grid>

        </Box>
        <ReportButtons
          boardType={"Board Insight"}
          collectedData={{
            ...districtInsightData,
            comparisonCards: comparisonCardsInfo.current
          }}
        />
      </LayoutPage>
    </DistrictInsightContext.Provider>
  )
}

export default DistrictInsightBoard;
